import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { ArticleService } from '@/api/generated/services/ArticleService'
import { ArticleCreateRequest } from '@/api/generated/models/ArticleCreateRequest'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import SearchableMemberSelect from '@/pages/project/TaskManagement/components/SearchableMemberSelect'

interface MeetingCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const MeetingCreatePanel: React.FC<MeetingCreatePanelProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [assigneeName, setAssigneeName] = useState('')
  const [assigneeEmail, setAssigneeEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { showError, showSuccess } = useAlert()

  // Get Redux state
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const currentUser = useAppSelector(state => state.auth.orgMember)

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('')
      setContent('')
      setAssigneeId('')
      setAssigneeName('')
      setAssigneeEmail('')
    }
  }, [isOpen])

  const handleAssigneeSelect = (memberId: string, memberName: string, memberEmail: string) => {
    setAssigneeId(memberId)
    setAssigneeName(memberName)
    setAssigneeEmail(memberEmail)
  }

  const handleCancel = () => {
    onClose()
  }

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      showError('제목을 입력해주세요.')
      return
    }

    if (!content.trim()) {
      showError('내용을 입력해주세요.')
      return
    }

    if (!assigneeId) {
      showError('담당자를 선택해주세요.')
      return
    }

    if (!selectedWorkspace?.id) {
      showError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    if (!currentUser?.id) {
      showError('사용자 정보를 확인할 수 없습니다.')
      return
    }

    setIsSubmitting(true)

    try {
      const createRequest: ArticleCreateRequest = {
        workspace_id: selectedWorkspace.id,
        creator_id: currentUser.id,
        assignee_id: assigneeId,
        title: title.trim(),
        content: content.trim()
      }

      await ArticleService.appsArticleViewsCreateArticle({
        requestBody: createRequest
      })

      showSuccess('회의록이 성공적으로 생성되었습니다.')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Failed to create meeting note:', error)
      showError('회의록 생성 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formValid = title.trim() && content.trim() && assigneeId

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !formValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="회의록 생성"
      width={800}
      headerActions={headerActions}
    >
      <div className="px-6 py-6 space-y-8 overflow-y-auto flex-1">
        {/* 기본 정보 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="기본 정보" />
          <div className="space-y-4">
            {/* 워크스페이스 정보 */}
            <InfoField
              label="워크스페이스"
              value={selectedWorkspace?.name || '-'}
            />

            {/* 생성자 정보 */}
            <InfoField
              label="생성자"
              value={currentUser?.account_name || '-'}
            />

            {/* 담당자 선택 */}
            <InfoField
              label="담당자"
              value={
                <SearchableMemberSelect
                  selectedMemberId={assigneeId || null}
                  selectedMemberName={assigneeName || null}
                  onSelect={handleAssigneeSelect}
                  disabled={isSubmitting}
                  placeholder="담당자를 선택해주세요"
                  searchPlaceholder="담당자 이름으로 검색..."
                  workspaceId={selectedWorkspace?.id || ''}
                  minSearchLength={1}
                  maxDisplayResults={10}
                />
              }
              fullWidth
            />
          </div>
        </div>

        {/* 회의록 내용 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="회의록 내용" />
          <div className="space-y-4">
            {/* 제목 입력 */}
            <InfoField
              label="제목"
              value={
                <Input
                  placeholder="회의록 제목을 입력해주세요"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                  className="w-full"
                />
              }
              fullWidth
            />

            {/* 내용 입력 */}
            <InfoField
              label="내용"
              value={
                <textarea
                  placeholder="회의록 내용을 입력해주세요"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border rounded resize-none outline-none"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    borderColor: designTokens.colors.gray[1],
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default MeetingCreatePanel